import { type Schema, type Document, type Block } from "@/zero/schema";

import type { JSONValue, Query, Zero } from "@rocicorp/zero";
import { v4 as uuidv4 } from "uuid";

type NewDocument = Omit<Document, "id">;
type NewBlock = Omit<Block, "id">;

type DocumentQuery = Query<
  Schema,
  "document",
  (Document & { blocks: readonly Block[] | undefined }) | undefined
>;

type DocumentIndexQuery = Query<Schema, "document", Document>;

export class DocumentQueryRepository {
  #z: Zero<Schema>;

  constructor(z: Zero<Schema>) {
    this.#z = z;
  }

  #generateId(): string {
    return uuidv4();
  }

  getDocumentIndex(owner: string, limit: number = 100): DocumentIndexQuery {
    return this.#z.query.document.where("owner", owner).limit(limit);
  }

  getDocumentById(id: string): DocumentQuery {
    return this.#z.query.document.related("blocks").where("id", id).one();
  }

  createDocument(doc: NewDocument) {
    const id = this.#generateId();
    this.#z.mutate.document.insert({ ...doc, id });
  }

  createBlock(block: NewBlock) {
    const id = this.#generateId();

    this.#z.mutate.block.insert({ ...block, id });
  }

  appendBlock(document: Document, block: NewBlock) {
    const blockId = this.#generateId();

    this.#z.mutate.block.insert({ ...block, id: blockId });
    // FIXME: There is the possibility of data loss when two clients create blocks:
    // when the .content key is updated in both blocks simultaneously, one of the updates
    // will be lost. The append here is not an actual append as it overwrites the content
    // array using the current value.
    this.#z.mutate.document.update({
      id: document.id,
      content: Array.isArray(document.content)
        ? [...document.content, blockId]
        : [blockId],
    });
  }

  dropBlock(document: Document, blockId: string) {
    this.#z.mutate.document.update({
      id: document.id,
      content: Array.isArray(document.content)
        ? document.content.filter((id) => id !== blockId)
        : [],
    });
    this.#z.mutate.block.update({ id: blockId, deleted: true });
  }

  setBlockPosition(document: Document, blockId: string, index: number) {
    const content = Array.isArray(document.content)
      ? [...document.content]
      : [];

    const currentIndex = content.indexOf(blockId);
    if (currentIndex === -1 || currentIndex === index) return;

    // Remove from current position
    content.splice(currentIndex, 1);

    // Insert at new position, adjusting for removal
    const adjustedIndex = currentIndex < index ? index - 1 : index;
    content.splice(adjustedIndex, 0, blockId);

    this.#z.mutate.document.update({ id: document.id, content });
  }

  // Add a child block to a parent block's content array
  appendBlockToBlock(parentBlock: Block, childBlock: NewBlock) {
    const childId = this.#generateId();

    // Create the child block
    this.#z.mutate.block.insert({ ...childBlock, id: childId });

    // Add child ID to parent's content array
    this.#z.mutate.block.update({
      id: parentBlock.id,
      content: Array.isArray(parentBlock.content)
        ? [...parentBlock.content, childId]
        : [childId],
    });

    return childId;
  }

  // Remove a child block from parent's content array
  removeBlockFromBlock(parentBlock: Block, childId: string) {
    // Remove from parent's content array
    this.#z.mutate.block.update({
      id: parentBlock.id,
      content: Array.isArray(parentBlock.content)
        ? parentBlock.content.filter((id) => id !== childId)
        : [],
    });

    // Mark child as deleted
    this.#z.mutate.block.update({ id: childId, deleted: true });
  }

  // Update block attributes
  updateBlockAttributes(
    blockId: string,
    attributes: Record<string, JSONValue>,
  ) {
    this.#z.mutate.block.update({
      id: blockId,
      attributes,
    });
  }

  // Update block content array
  updateBlockContent(blockId: string, content: string[]) {
    this.#z.mutate.block.update({
      id: blockId,
      content,
    });
  }

  deleteDocument(documentId: string) {
    this.#z.mutate.document.delete({ id: documentId });
  }

  renameDocument(documentId: string, name: string) {
    this.#z.mutate.document.update({ id: documentId, name });
  }
}
